package fr.frux.feature.map

import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.maps.Location
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

/** Clé de la dernière fermeture de la carte de zone vide. Une seule : une zone fermée à la fois suffit. */
internal const val EMPTY_AREA_DISMISSED_KEY = "map_empty_area_dismissed"

/** Silence après une fermeture (spec onboarding de contribution, principe 5). */
internal val EMPTY_AREA_SILENCE: Duration = 7.days

/** Au-delà, c'est une autre zone : la carte peut revenir avant la fin du silence. */
internal const val EMPTY_AREA_SILENCE_RADIUS_METERS = 2_000.0

/** Distance sous laquelle le plus proche est « un peu plus loin » plutôt qu'ailleurs. */
internal const val NEAR_TREE_THRESHOLD_KM = 3.0

/** Fermeture de la carte de zone vide : où et quand. */
data class EmptyAreaDismissal(
    val center: Location,
    val at: Instant,
)

/** Ordre des actions de la carte : recenser d'abord, ou montrer d'abord les arbres proches. */
internal enum class EmptyAreaEmphasis { CONTRIBUTE, DISCOVER }

internal fun emptyAreaEmphasis(nearestDistanceMeters: Double?): EmptyAreaEmphasis =
    if (nearestDistanceMeters != null && nearestDistanceMeters < NEAR_TREE_THRESHOLD_KM * 1_000) {
        EmptyAreaEmphasis.DISCOVER
    } else {
        EmptyAreaEmphasis.CONTRIBUTE
    }

/**
 * La carte reste tue pour [center] tant que la fermeture date de moins de [EMPTY_AREA_SILENCE] et
 * vise un centre à moins de [EMPTY_AREA_SILENCE_RADIUS_METERS]. Une horloge reculée (date de
 * fermeture dans le futur) ne tait rien : mieux vaut une invitation de trop qu'un silence sans fin.
 */
internal fun isEmptyAreaSilenced(
    dismissal: EmptyAreaDismissal?,
    center: Location,
    now: Instant,
): Boolean {
    if (dismissal == null) return false
    val elapsed = now - dismissal.at
    return elapsed >= Duration.ZERO &&
        elapsed < EMPTY_AREA_SILENCE &&
        dismissal.center.distanceMetersTo(center) <= EMPTY_AREA_SILENCE_RADIUS_METERS
}

/**
 * Mémoire de la fermeture, entre deux lancements. Même discipline que [MapViewStore] : toute erreur
 * de stockage est avalée, une lecture ratée rend `null` et la carte s'affiche.
 */
class EmptyAreaDismissStore(
    private val preferences: AppPreferences,
) {
    suspend fun load(): EmptyAreaDismissal? =
        runCatchingCancellable { preferences.getString(EMPTY_AREA_DISMISSED_KEY) }
            .onFailure { Logger.e(TAG, "Lecture de la fermeture de zone vide impossible", it) }
            .getOrNull()
            ?.let(::parse)

    suspend fun save(dismissal: EmptyAreaDismissal) {
        val raw =
            listOf(dismissal.center.latitude, dismissal.center.longitude, dismissal.at.toEpochMilliseconds())
                .joinToString(SEPARATOR)
        runCatchingCancellable { preferences.putString(EMPTY_AREA_DISMISSED_KEY, raw) }
            .onFailure { Logger.e(TAG, "Écriture de la fermeture de zone vide impossible", it) }
    }

    suspend fun clear() {
        runCatchingCancellable { preferences.remove(EMPTY_AREA_DISMISSED_KEY) }
            .onFailure { Logger.e(TAG, "Purge de la fermeture de zone vide impossible", it) }
    }

    private fun parse(raw: String): EmptyAreaDismissal? {
        val parts = raw.split(SEPARATOR)
        if (parts.size != 3) return null
        return build(parts[0].toDoubleOrNull(), parts[1].toDoubleOrNull(), parts[2].toLongOrNull())
    }

    private fun build(
        rawLatitude: Double?,
        rawLongitude: Double?,
        millis: Long?,
    ): EmptyAreaDismissal? {
        val latitude = rawLatitude?.takeIf { it in -90.0..90.0 }
        val longitude = rawLongitude?.takeIf { it in -180.0..180.0 }
        return if (latitude != null && longitude != null && millis != null) {
            EmptyAreaDismissal(Location(latitude, longitude), Instant.fromEpochMilliseconds(millis))
        } else {
            null
        }
    }

    private companion object {
        const val TAG = "EmptyAreaDismissStore"
        const val SEPARATOR = ";"
    }
}

/**
 * Fermeture de la carte de zone vide côté ViewModel, sortie de [MapViewModel] comme
 * [NearestDistanceLoader] (ratchet detekt `LargeClass`). Lit le store une fois, puis tient la
 * fermeture en mémoire.
 */
internal class EmptyAreaDismissController(
    viewStore: MapViewStore,
    private val launchForUser: (suspend CoroutineScope.() -> Unit) -> Job,
    private val state: MutableStateFlow<MapState>,
    private val now: () -> Instant,
) {
    private val store = viewStore.emptyAreaDismissals
    private var dismissal: EmptyAreaDismissal? = null
    private var loaded = false

    suspend fun isSilenced(center: Location): Boolean {
        if (!loaded) {
            dismissal = store.load()
            loaded = true
        }
        return isEmptyAreaSilenced(dismissal, center, now())
    }

    /** Tait la carte tout de suite dans l'état, puis mémorise la fermeture. */
    fun dismiss(center: Location) {
        state.update { it.copy(emptyAreaSilenced = true) }
        val fresh = EmptyAreaDismissal(center, now())
        dismissal = fresh
        loaded = true
        launchForUser { store.save(fresh) }
    }

    /** Déconnexion : la fermeture est une position, elle ne passe pas au compte suivant. */
    fun forget() {
        dismissal = null
        loaded = true
        launchForUser { store.clear() }
    }
}
